import React, { useState } from 'react';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { useKitView } from 'containers/KitViewContext';
import type { IAssembly } from 'containers/KitWorkspaceStorage/types';
import { filterAssemblies, filterKits } from 'common/components/kit-shared/kitFilter';
import { useKitStartOver } from 'common/components/kit-shared/useKitStartOver';
import { useKitLocked } from 'common/components/kit-shared/useKitLocked';

import classNames from 'classnames';
import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import BottomSheet from 'common/components/kit-shared/BottomSheet';
import StandaloneAssemblyList from 'common/components/kit-shared/StandaloneAssemblyList';
import KitPicker from 'common/components/kit-shared/KitPicker';
import CombinedPdfPreview from 'common/components/kit-shared/CombinedPdfPreview';
import KitCard from './KitCard';
import styles from './styles.module.scss';

/**
 * Full-screen overlay (mobile-first) for organizing the workspace: My
 * assemblies + My kits, kit cards, inline kit creation. The primary action opens
 * the shared Combined PDF preview popup (read-only) where the whole-workspace PDF
 * is generated. Start over resets this workspace, keeping the empty kit shells,
 * and restarts the wizard. Once the PDF is generated the body actions are locked;
 * only Preview (footer) and Start over (header) stay live.
 */
const ManageKitsPage = () => {
  const t = useKitTranslate();
  const { closeManageKits, openMyAssemblies } = useKitView();
  const { workspaceKey, kits, standalone } = useKitWorkspace();
  const { requestStartOver, dialog: startOverDialog, isResetting } = useKitStartOver();
  const locked = useKitLocked();

  const [pickerFor, setPickerFor] = useState<IAssembly | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [query, setQuery] = useState('');

  // Total assemblies in the workspace (standalone + every kit line); each
  // assembly is standalone XOR in exactly one kit, so this is the full count.
  const assemblyCount = standalone.length + kits.reduce((sum, k) => sum + k.lines.length, 0);

  // US-18: text filter over saved assemblies and kit lines by name, part, spec.
  const filteredStandalone = filterAssemblies(standalone, query);
  const filteredKits = filterKits(kits, query);

  const empty = kits.length === 0 && standalone.length === 0;

  return (
    <BottomSheet
      title={t('texts.kits.manageTitle')}
      subtitle={t('texts.kits.manageSubtitle')}
      onClose={closeManageKits}
      fullScreen
      headerAction={!empty ? (
        <>
          <button
            type="button"
            className={styles.myAssembliesBtn}
            onClick={openMyAssemblies}
          >
            {t('texts.kits.manageMyAssembliesBtn')}
            <span className={styles.headerCount}>{assemblyCount}</span>
          </button>
          <button
            type="button"
            className={styles.startOverBtn}
            onClick={requestStartOver}
            disabled={isResetting}
          >
            {`↺ ${t('texts.kits.startOver')}`}
          </button>
        </>
      ) : undefined}
      footer={(
        <div className={styles.finishFooter}>
          <Button
            schema={ButtonSchema.primary}
            size={ButtonSize.full}
            className={styles.finishBtn}
            onClick={() => setShowPreview(true)}
            disabled={empty}
          >
            {t('texts.kits.pdfPreview')}
          </Button>
        </div>
      )}
    >
      {/* Body actions (add to kit, edit, delete, qty, search) are locked once the
          PDF is generated; Preview (footer) + Start over (header) stay live.
          Portaled popups below escape pointer-events via #modal-root. */}
      <div className={classNames({ [styles.locked]: locked })} aria-disabled={locked}>
        {(standalone.length > 0 || kits.length > 0) && (
          <div className={styles.searchRow}>
            <input
              className={styles.searchInput}
              type="search"
              value={query}
              placeholder={t('texts.kits.searchPlaceholder')}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </div>
        )}

        {/* Single scroll (no tabs): My assemblies block first, then Kits. */}
        <div className={styles.section}>
          <h3 className={styles.sectionHeader}>
            {t('texts.kits.tabs.assemblies', { count: filteredStandalone.length })}
          </h3>
          <StandaloneAssemblyList
            assemblies={filteredStandalone}
            onAddToKit={(a) => setPickerFor(a)}
            emptyText={query.trim() ? t('texts.kits.noResults') : undefined}
          />
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionHeader}>
            {t('texts.kits.tabs.kits', { count: filteredKits.length })}
          </h3>
          <div className={styles.kitsTab}>
            {filteredKits.length === 0 ? (
              <p className={styles.empty}>
                {query.trim() ? t('texts.kits.noResults') : t('texts.kits.kits.empty')}
              </p>
            ) : (
              filteredKits.map((kit) => <KitCard key={kit.uuid} kit={kit} query={query} />)
            )}
          </div>
        </div>
      </div>

      {pickerFor && workspaceKey && (
        <KitPicker
          workspaceKey={workspaceKey}
          assembly={pickerFor}
          onClose={() => setPickerFor(null)}
        />
      )}

      {showPreview && <CombinedPdfPreview onClose={() => setShowPreview(false)} />}

      {startOverDialog}
    </BottomSheet>
  );
};

export default ManageKitsPage;
